import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiCode, FiUsers, FiCpu, FiSettings, FiDatabase, FiCloud } from 'react-icons/fi';
import './ServicesOverview.css';

const ServicesOverview = () => {
  const navigate = useNavigate();

  const otherServices = [
    {
      id: 1,
      icon: <FiCode />,
      title: "Software Development",
      description: "Custom software solutions and development services to accelerate your digital transformation",
      features: ["Custom Applications", "Web Development", "Mobile Apps", "API Development"],
      color: "#7C3AED",
      route: "/solutions/software-development"
    },
    {
      id: 2,
      icon: <FiCpu />,
      title: "AI & Machine Learning",
      description: "Advanced artificial intelligence and machine learning solutions for business automation",
      features: ["AI Strategy", "ML Models", "Automation", "Predictive Analytics"],
      color: "#DC2626",
      route: "/solutions/ai-machine-learning"
    },
    {
      id: 3,
      icon: <FiCloud />,
      title: "Cloud Solutions",
      description: "Comprehensive cloud infrastructure and migration services for scalable business growth",
      features: ["Cloud Migration", "Infrastructure Setup", "Scalability Solutions", "Cost Optimization"],
      color: "#EA580C",
      route: "/solutions/cloud-solutions"
    },
    {
      id: 4,
      icon: <FiSettings />,
      title: "DevOps & SRE",
      description: "DevOps and Site Reliability Engineering services for robust, scalable operations",
      features: ["CI/CD Pipelines", "Infrastructure as Code", "Monitoring & Alerting", "Performance Optimization"],
      color: "#10B981",
      route: "/solutions/devops-sre"
    },
    {
      id: 5,
      icon: <FiDatabase />,
      title: "Data Analytics",
      description: "Advanced data analytics and business intelligence for strategic decision-making",
      features: ["Data Analytics", "Business Intelligence", "Predictive Modeling", "Data Governance"],
      color: "#0891B2",
      route: "/solutions/data-analytics"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const handleExploreService = (route) => {
    navigate(route);
    window.scrollTo(0, 0);
  };

  return (
    <section className="services-overview">
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="section-badge">
            <FiSettings />
            <span>Enterprise Solutions</span>
          </div>
          <h2 className="section-title">
            Complete Business <span className="gradient-text">Technology Solutions</span>
          </h2>
          <p className="section-subtitle">
            Accelerate your business growth with our comprehensive technology and strategic consulting services
          </p>
        </motion.div>

        <motion.div 
          className="overview-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {otherServices.map((service, index) => (
            <motion.div
              key={service.id}
              className="overview-card"
              variants={cardVariants}
              whileHover={{ 
                y: -15,
                rotateY: 5,
                transition: { duration: 0.3 }
              }}
            >
              <div 
                className="card-background"
                style={{ background: `linear-gradient(135deg, ${service.color}15, ${service.color}05)` }}
              ></div>
              
              <div className="card-content">
                <div 
                  className="service-icon-large"
                  style={{ color: service.color }}
                >
                  {service.icon}
                </div>
                
                <h3 className="service-title-large">{service.title}</h3>
                <p className="service-desc-large">{service.description}</p>
                
                <div className="features-list">
                  {service.features.map((feature, idx) => (
                    <motion.div 
                      key={idx}
                      className="feature-tag"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      style={{ borderColor: service.color }}
                    >
                      {feature}
                    </motion.div>
                  ))}
                </div>

                <motion.button 
                  className="overview-btn"
                  style={{ backgroundColor: service.color }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleExploreService(service.route)}
                >
                  Explore Service
                </motion.button>
              </div>

              <div 
                className="card-shine"
                style={{ background: `linear-gradient(45deg, transparent, ${service.color}20, transparent)` }}
              ></div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesOverview;
